import { type FC, useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from '@pypestream/translations';

import {
  CHART_METRICS,
  type ChartMetricKey,
  type PerfMetricRow,
} from './types';

const NS = 'pro-studio/performance';

export interface PerfChartProps {
  rows: PerfMetricRow[];
  scenario: string;
  metricKey: ChartMetricKey;
}

// Indexed by position in the sorted `sizes` array — picks up new dataset
// sizes (e.g. 2500/7500) automatically instead of falling back to grey when
// the bench adds rows. Fallback hex values match the original palette.
const LINE_PALETTE: string[] = [
  'var(--ps-color-chart-1, #4f46e5)',
  'var(--ps-color-chart-2, #10b981)',
  'var(--ps-color-chart-3, #f59e0b)',
  'var(--ps-color-chart-4, #ef4444)',
  'var(--ps-color-chart-5, #06b6d4)',
];
const FALLBACK_LINE_COLOR = 'var(--ps-color-chart-default, #6b7280)';

function pickLineColor(index: number): string {
  return LINE_PALETTE[index] ?? FALLBACK_LINE_COLOR;
}

/**
 * Time-series of a single bench metric, one line per dataset size.
 *
 * Plots from oldest to newest along the x-axis. Filters rows to the
 * supplied scenario and skips any whose settings.size is undefined.
 */
export const PerfChart: FC<PerfChartProps> = ({
  rows,
  scenario,
  metricKey,
}) => {
  const [t] = useTranslation();
  const metric = useMemo(
    () => CHART_METRICS.find((m) => m.key === metricKey),
    [metricKey]
  );

  const { data, sizes } = useMemo(() => {
    const filtered = rows
      .filter((r) => r.settings?.scenario === scenario)
      .filter(
        (r) =>
          typeof r.settings?.size === 'number' &&
          Number.isFinite(r.settings.size)
      );

    const sizeSet = new Set<number>();
    const byCreatedAt = new Map<
      string,
      { ts: string; label: string } & Record<string, number | string | null>
    >();

    for (const row of filtered) {
      const size = row.settings?.size as number;
      sizeSet.add(size);
      const value = metric?.pick(row.settings) ?? null;
      const key = row.createdAt;
      const existing = byCreatedAt.get(key);
      const sha =
        row.settings?.__meta?.ci?.sha?.slice(0, 7) ??
        row.settings?.commit ??
        '';
      const label =
        `${new Date(row.createdAt).toLocaleString()} ${sha ? `(${sha})` : ''}`.trim();
      const next = existing ?? { ts: key, label };
      next[`size_${size}`] = value;
      byCreatedAt.set(key, next);
    }

    return {
      data: Array.from(byCreatedAt.values()).sort((a, b) =>
        a.ts.localeCompare(b.ts)
      ),
      sizes: Array.from(sizeSet).sort((a, b) => a - b),
    };
    // `metric` is memoized above and only changes when metricKey changes, so
    // this useMemo no longer re-runs on every render the way it did when
    // metric was a fresh `.find()` result.
  }, [rows, scenario, metric]);

  if (data.length === 0) {
    return (
      <div
        style={{
          padding: 32,
          textAlign: 'center',
          color: 'var(--ps-color-text-subtle, #6b7280)',
          fontStyle: 'italic',
        }}
      >
        {t(`${NS}:chart.noData`, { scenario })}
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={320}>
      <LineChart
        data={data}
        margin={{ top: 16, right: 24, bottom: 16, left: 0 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="var(--ps-color-border, #e5e7eb)"
        />
        <XAxis
          dataKey="label"
          fontSize={11}
          interval="preserveStartEnd"
          tickFormatter={(v: string) => v.split(' ').slice(0, 2).join(' ')}
        />
        <YAxis
          fontSize={11}
          label={{
            value: `${metric?.label ?? metricKey}${
              metric?.unit ? ` (${metric.unit})` : ''
            }`,
            angle: -90,
            position: 'insideLeft',
            style: {
              fontSize: 11,
              fill: 'var(--ps-color-text-subtle, #6b7280)',
            },
          }}
        />
        <Tooltip
          formatter={(value: number | string) =>
            typeof value === 'number' ? value.toFixed(2) : value
          }
        />
        <Legend />
        {sizes.map((size, i) => (
          <Line
            key={size}
            type="monotone"
            dataKey={`size_${size}`}
            name={`${size.toLocaleString()} rows`}
            stroke={pickLineColor(i)}
            strokeWidth={2}
            dot={{ r: 3 }}
            connectNulls
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
